/**
 * 购物车推荐商品组件
 * 支持基于购物车商品的智能推荐和系统热门商品推荐
 */

class CartRecommendations {
    constructor(containerId, options = {}) {
        this.containerId = containerId;
        this.container = document.getElementById(containerId);
        this.swiper = null; // 存储Swiper实例
        this.options = {
            limit: options.limit || 8,
            showPrice: options.showPrice !== false,
            cssPrefix: 'recommendations',
            cartItems: options.cartItems || [],
            shopLocale: options.shopLocale || 'en',
            shopCurrency: options.shopCurrency || 'USD',
            ...options
        };

        if (this.container) {
            if (this.container.cartRecommendationsInstance) {
                return this.container.cartRecommendationsInstance;
            }

            this.container.cartRecommendationsInstance = this;
            this.container.dataset.recommendationsLoading = 'true';

            if (this.container.dataset.staticProducts === 'true' && this.container.querySelector('.swiper-slide')) {
                this.markLoaded();
                this.initSwiper();
                this.initializeFavoriteButtons();
                return;
            }

            this.init();
        }
    }

    init() {
        let productId = null;

        try {
            const recentlyViewed = JSON.parse(localStorage.getItem('recently-viewed-products') || '[]');
            if (Array.isArray(recentlyViewed) && recentlyViewed.length > 0) {
                productId = recentlyViewed[0];
            }
        } catch (error) {
            console.warn('Unable to read recently viewed products:', error);
        }
        
        // 如果没有最近浏览的产品，随机获取一个产品
        if (!productId) {
            this.getRandomProductAndRecommend();
            return;
        }
        
        const recommendationsUrl = `/recommendations/products.json?product_id=${encodeURIComponent(productId)}&limit=${this.options.limit}`;

        fetch(recommendationsUrl)
            .then(response => {
                if (!response.ok) throw new Error(`Recommendations request failed: ${response.status}`);
                return response.json();
            })
            .then(data => this.handleProductsData(data))
            .catch(error => this.handleError(error));
    }

    /**
     * 获取随机产品并基于它获取推荐
     */
    getRandomProductAndRecommend() {
        const fallbackUrl = `${window.Shopify.routes.root}collections/all/products.json?limit=50`;
        
        fetch(fallbackUrl)
            .then(response => {
                if (!response.ok) throw new Error(`Products request failed: ${response.status}`);
                return response.json();
            })
            .then(data => {
                const products = data.products || [];
                if (products && products.length > 0) {
                    // 随机选择一个产品
                    const randomIndex = Math.floor(Math.random() * products.length);
                    const randomProduct = products[randomIndex];
                    
                    // 基于随机产品获取推荐
                    const recommendationsUrl = `/recommendations/products.json?product_id=${randomProduct.id}&limit=${this.options.limit}`;
                    
                    return fetch(recommendationsUrl)
                        .then(response => {
                            if (!response.ok) throw new Error(`Recommendations request failed: ${response.status}`);
                            return response.json();
                        })
                        .then(data => this.handleProductsData(data))
                        .catch(error => {
                            // 如果推荐API失败，使用备用方案
                            console.warn('Failed to get recommendations, using fallback');
                            this.tryFallbackRecommendations();
                        });
                } else {
                    // 如果没有产品，使用备用方案
                    this.tryFallbackRecommendations();
                }
            })
            .catch(error => {
                console.error('Failed to get random product:', error);
                this.tryFallbackRecommendations();
            });
    }

    handleProductsData(data) {
        // 处理不同API返回的数据结构
        const products = Array.isArray(data?.products) ? data.products : [];
        const productIds = products
            .map(item => `id:${item.id}`)
            .filter(item => item !== 'id:9217548517629');

        if (productIds.length === 0) {
            this.tryFallbackRecommendations();
            return;
        }

        fetch(`/search?type=product&q=${encodeURIComponent(productIds.join(' OR '))}`)
            .then(response => {
                if (!response.ok) throw new Error(`Product search failed: ${response.status}`);
                return response.text();
            })
            .then(data => {
                if (!this.container.isConnected) return;

                const dataHtml = new DOMParser().parseFromString(data, 'text/html');
                const productList = dataHtml.querySelector('.product-grid');
                if (!productList) {
                    this.tryFallbackRecommendations();
                    return;
                }

                // 获取swiper-wrapper容器
                const swiperWrapper = this.container.querySelector('.swiper-wrapper');
                if (swiperWrapper) {
                    // 将产品项包裹在swiper-slide中
                    const productItems = productList.querySelectorAll('.grid__item');
                    if (productItems.length === 0) {
                        this.tryFallbackRecommendations();
                        return;
                    }

                    swiperWrapper.replaceChildren();
                    productItems.forEach(item => {
                        const slide = document.createElement('div');
                        slide.className = 'swiper-slide';
                        slide.appendChild(item);
                        swiperWrapper.appendChild(slide);
                    });
                } else {
                    // 如果不是swiper容器，使用原来的方式
                    this.container.innerHTML = productList.outerHTML;
                }

                // 初始化Swiper
                this.markLoaded();
                this.initSwiper();
            })
            .catch(error => this.handleError(error));
    }

    generateProductsHTML(products) {
        let productsHTML = '';

        products.forEach(product => {
            // 过滤掉已在购物车中的商品（如果购物车不为空）
            if (!this.options.cartItems || this.options.cartItems.length === 0 || !this.options.cartItems.includes(product.id)) {
                productsHTML += this.generateProductHTML(product);
            }
        });

        return productsHTML;
    }

    generateProductHTML(product) {
        const productUrl = this.escapeHtml(`${window.Shopify.routes.root}products/${encodeURIComponent(product.handle || '')}`);
        const productTitle = this.escapeHtml(product.title || '');
        const productId = this.escapeHtml(product.id || '');
        const imageUrl = this.escapeHtml(this.getImageUrl(product.featured_image || product.images?.[0]));
        const price = this.formatPrice(product);
        const secondaryImage = this.escapeHtml(this.getImageUrl(product.images?.[1]));
        return `
      <div class="${this.options.cssPrefix}__item-wrapper">
        <a href="${productUrl}" class="${this.options.cssPrefix}__item">
          <div class="${this.options.cssPrefix}__media">
            <div class="media media--transparent media--hover-effect">
              ${imageUrl ? `<img src="${imageUrl}" alt="${productTitle}" class="${this.options.cssPrefix}__item-image motion-reduce" loading="lazy">` : ''}
              ${secondaryImage ? `<img src="${secondaryImage}" alt="" class="${this.options.cssPrefix}__item-image ${this.options.cssPrefix}__item-image--secondary motion-reduce" loading="lazy">` : ''}
            </div>
          </div>
          <div class="${this.options.cssPrefix}__item-info">
            <div class="${this.options.cssPrefix}__item-title">${productTitle}</div>
            ${this.options.showPrice && price ? `<div class="${this.options.cssPrefix}__item-price">${price}</div>` : ''}
          </div>
          <div class="${this.options.cssPrefix}__item-badge">
            <div class="${this.options.cssPrefix}__item-favorites" data-product-id="${productId}">
              <!-- 收藏按钮将通过JavaScript动态添加 -->
            </div>
          </div>
        </a>
      </div>
    `;
    }

    formatPrice(product) {
        if (!this.options.showPrice) return '';

        try {
            const centsRawValue = product.price ?? product.price_min;
            const centsValue = centsRawValue === undefined || centsRawValue === null || centsRawValue === ''
                ? Number.NaN
                : Number(centsRawValue);
            const variants = Array.isArray(product.variants) ? product.variants : [];
            const availableVariants = variants.filter(variant => variant.available !== false);
            const priceVariants = availableVariants.length > 0 ? availableVariants : variants;
            const variantPrices = priceVariants
                .map(variant => Number(variant.price))
                .filter(price => Number.isFinite(price));
            const variantPrice = variantPrices.length > 0 ? Math.min(...variantPrices) : Number.NaN;
            const priceValue = Number.isFinite(centsValue)
                ? centsValue / 100
                : variantPrice;

            if (!Number.isFinite(priceValue)) return '';
            return new Intl.NumberFormat(this.options.shopLocale, {
                style: 'currency',
                currency: this.options.shopCurrency
            }).format(priceValue);
        } catch (error) {
            return '';
        }
    }

    getImageUrl(image) {
        if (!image) return '';
        if (typeof image === 'string') return image;
        return image.src || image.url || '';
    }

    escapeHtml(value) {
        return String(value)
            .replaceAll('&', '&amp;')
            .replaceAll('<', '&lt;')
            .replaceAll('>', '&gt;')
            .replaceAll('"', '&quot;')
            .replaceAll("'", '&#39;');
    }

    hasAvailableVariant(product) {
        const variants = Array.isArray(product?.variants) ? product.variants : [];
        return variants.length === 0 || variants.some(variant => variant.available !== false);
    }

    initializeFavoriteButtons() {
        const favoriteContainers = this.container.querySelectorAll(`.${this.options.cssPrefix}__item-favorites`);
        favoriteContainers.forEach(container => {
            const productId = container.getAttribute('data-product-id');
            if (productId && typeof window.renderFavoriteButton === 'function') {
                window.renderFavoriteButton(container, productId);
            }
        });
    }

    tryFallbackRecommendations() {
        const fallbackUrl = `${window.Shopify.routes.root}collections/all/products.json?limit=50`;

        fetch(fallbackUrl)
            .then(response => {
                if (!response.ok) throw new Error(`Fallback products request failed: ${response.status}`);
                return response.json();
            })
            .then(data => {
                if (!this.container.isConnected) return;

                const products = data.products || [];
                if (products && products.length > 0) {
                    const productsHTML = this.generateFallbackProductsHTML(products);

                    if (productsHTML) {
                        const swiperWrapper = this.container.querySelector('.swiper-wrapper');
                        if (swiperWrapper) {
                            swiperWrapper.innerHTML = productsHTML;
                            this.markLoaded();
                            this.initSwiper();
                        } else {
                            this.container.innerHTML = productsHTML;
                            this.markLoaded();
                        }
                        this.initializeFavoriteButtons();
                    } else {
                        this.hideRecommendations();
                    }
                } else {
                    this.hideRecommendations();
                }
            })
            .catch(error => this.hideRecommendations());
    }

    generateFallbackProductsHTML(products) {
        let productsHTML = '';
        const availableProducts = products.filter(product => this.hasAvailableVariant(product));
        const productsToRender = availableProducts.length > 0 ? availableProducts : products;

        productsToRender.slice(0, this.options.limit).forEach(product => {
            const productUrl = this.escapeHtml(`${window.Shopify.routes.root}products/${encodeURIComponent(product.handle || '')}`);
            const productTitle = this.escapeHtml(product.title || '');
            const productId = this.escapeHtml(product.id || '');
            const imageUrl = this.escapeHtml(this.getImageUrl(product.featured_image || product.images?.[0]));
            const price = this.formatPrice(product);

            const secondaryImage = this.escapeHtml(this.getImageUrl(product.images?.[1]));

            productsHTML += `
        <div class="swiper-slide">
        <div class="${this.options.cssPrefix}__item-wrapper">
          <a href="${productUrl}" class="${this.options.cssPrefix}__item">
            <div class="${this.options.cssPrefix}__media">
              <div class="media media--transparent media--hover-effect">
                ${imageUrl ? `<img src="${imageUrl}" alt="${productTitle}" class="${this.options.cssPrefix}__item-image motion-reduce" loading="lazy">` : ''}
                ${secondaryImage ? `<img src="${secondaryImage}" alt="" class="${this.options.cssPrefix}__item-image ${this.options.cssPrefix}__item-image--secondary motion-reduce" loading="lazy">` : ''}
              </div>
            </div>
            <div class="${this.options.cssPrefix}__item-info">
              <div class="${this.options.cssPrefix}__item-title">${productTitle}</div>
              ${this.options.showPrice && price ? `<div class="${this.options.cssPrefix}__item-price">${price}</div>` : ''}
            </div>
            <div class="${this.options.cssPrefix}__item-badge">
              <div class="${this.options.cssPrefix}__item-favorites" data-product-id="${productId}">
                <!-- 收藏按钮将通过JavaScript动态添加 -->
              </div>
            </div>
          </a>
        </div>
        </div>
      `;
        });

        return productsHTML;
    }

    handleError(error) {
        // 如果是空购物车，尝试备用方案
        console.error('Failed to load cart recommendations:', error);
        this.tryFallbackRecommendations();
    }

    markLoaded() {
        this.container.dataset.recommendationsLoading = 'false';
        this.container.dataset.recommendationsLoaded = 'true';
        const recommendationsSection = this.container.closest(`.${this.options.cssPrefix}`);
        if (recommendationsSection) {
            recommendationsSection.style.removeProperty('display');
        }
    }

    hideRecommendations() {
        this.container.dataset.recommendationsLoading = 'false';
        if (this.container.querySelector('.swiper-slide')) {
            this.container.dataset.recommendationsLoaded = 'true';
            return;
        }

        const recommendationsSection = this.container.closest(`.${this.options.cssPrefix}`);
        if (recommendationsSection) {
            recommendationsSection.style.display = 'none';
        }
    }

    /**
     * 初始化Swiper轮播
     */
    initSwiper() {
        // 确保Swiper库已加载
        if (typeof Swiper === 'undefined') {
            console.warn('Swiper library not found');
            return;
        }

        // 销毁已存在的实例
        if (this.swiper) {
            this.swiper.destroy(true, true);
        }

        // 检查是使用滚动条还是箭头
        const scrollbarEl = this.container.querySelector('.swiper-scrollbar');
        const prevButton = this.container.querySelector('.swiper-button-prev');
        const nextButton = this.container.querySelector('.swiper-button-next');
        const useScrollbar = !!scrollbarEl;
        // Swiper 配置
        const swiperConfig = {
            slidesPerView: this.options.slidesPerView || 4,
            spaceBetween: 20,
            loop: !useScrollbar, // 使用滚动条时不循环
            grabCursor: true,
            slidesPerGroup: 2,
            // 响应式断点
            breakpoints: {
                320: {
                    slidesPerView: 2,
                    spaceBetween: 10,
                },
                768: {
                    slidesPerView: 3,
                    spaceBetween: 15,
                },
                1024: {
                    slidesPerView: this.options.slidesPerView || 4,
                    spaceBetween: 20,
                }
            }
        };

        // 如果使用滚动条
        if (useScrollbar) {
            swiperConfig.scrollbar = {
                el: scrollbarEl,
                draggable: true,
            };
        }
        // 否则使用导航箭头
        else if (prevButton && nextButton) {
            swiperConfig.navigation = {
                nextEl: nextButton,
                prevEl: prevButton,
            };
        }

        // 初始化Swiper
        this.swiper = new Swiper(this.container, swiperConfig);
    }

    /**
     * 销毁实例
     */
    destroy() {
        if (this.swiper) {
            this.swiper.destroy(true, true);
            this.swiper = null;
        }
        if (this.container?.cartRecommendationsInstance === this) {
            delete this.container.cartRecommendationsInstance;
        }
    }
}

// 全局初始化函数
window.initCartRecommendations = function (containerId, options) {
    return new CartRecommendations(containerId, options);
};

window.initializeCartRecommendations = function (root = document) {
    const selector = '[id^="cart-recommendations-"]';
    const recommendationContainers = root.matches?.(selector)
        ? [root]
        : Array.from(root.querySelectorAll?.(selector) || []);

    recommendationContainers.forEach(container => {
        if (container.cartRecommendationsInstance || container.dataset.recommendationsLoading === 'true') {
            return;
        }

        container.dataset.recommendationsLoading = 'true';
        new CartRecommendations(container.id, {
            limit: parseInt(container.dataset.limit) || 8,
            showPrice: container.dataset.showPrice !== 'false',
            slidesPerView: parseInt(container.dataset.slidesPerView) || 4,
            showScrollbar: container.dataset.showScrollbar === 'true',
            cssPrefix: 'recommendations',
            cartItems: window.cartItems || [],
            shopLocale: window.Shopify?.locale || 'en',
            shopCurrency: window.Shopify?.currency?.active || 'USD'
        });
    });
};

// DOM加载完成后自动初始化
document.addEventListener('DOMContentLoaded', function () {
    window.initializeCartRecommendations(document);
});
